import type { GoalLevel, OfferExtraGoal, OfferLevelOption, OfferStationView } from '../../models/attendantGoalOffer';

type StationLevels = Pick<OfferStationView, 'placeId' | 'recommended' | 'levelChosen' | 'options'>;

/** Posto sem nível possível (options vazio) fica fora do seletor e do payload: a B7.3 exige uma chave por posto da sugestão, e só eles. */
export const selectableStations = <T extends StationLevels>(stations: T[]): T[] => stations.filter((s) => s.options.length > 0);

/** Nível inicial de cada posto: o já escolhido (convite reaberto) ou o recomendado pelo motor. */
export const initialLevels = (stations: StationLevels[]): Record<string, GoalLevel> =>
  Object.fromEntries(stations.map((s) => [s.placeId, s.levelChosen ?? s.recommended]));

/**
 * Corpo de `levels` do POST activate: uma chave para cada posto da sugestão e só eles (o backend recusa
 * chave a mais ou a menos). Nível ausente das opções (ex.: níveis fundidos) volta ao recomendado.
 */
export const levelsPayload = (
  stations: StationLevels[],
  chosen: Record<string, GoalLevel>,
): Record<string, GoalLevel> =>
  Object.fromEntries(stations.map((s) => {
    const level = chosen[s.placeId] ?? s.recommended;
    return [s.placeId, s.options.some((o) => o.level === level) ? level : s.recommended];
  }));

/**
 * `stale_suggestion`: a sugestão nova substitui a antiga, mas a escolha do dono é preservada. Para cada posto
 * da sugestão nova mantém o nível anterior se ele ainda está em `options`; senão o recomendado.
 * Postos que sumiram da sugestão saem; postos novos entram com o recomendado.
 */
export const mergeLevels = (
  stations: StationLevels[],
  previous: Record<string, GoalLevel>,
): Record<string, GoalLevel> =>
  Object.fromEntries(selectableStations(stations).map((s) => {
    const prev = previous[s.placeId];
    return [s.placeId, prev && s.options.some((o) => o.level === prev) ? prev : s.recommended];
  }));

/** Opção do nível escolhido (degustação ativa); sem escolha gravada, a recomendada. */
export const chosenOption = (s: StationLevels): OfferLevelOption | undefined =>
  s.options.find((o) => o.level === (s.levelChosen ?? s.recommended)) ?? s.options[0];

/** Valor recomendado de uma meta extra: `value`; se faltar, o da opção do nível recomendado (ou a primeira); sem opção, null. */
export const extraGoalValue = (goal: OfferExtraGoal): number | null => {
  if (Number.isFinite(goal.value)) return goal.value;
  const option = goal.options?.find((o) => o.level === goal.recommended) ?? goal.options?.[0];
  return option ? option.value : null;
};
